import React, { useContext, useState, useEffect, useMemo } from "react";
import Waitlist from "./Waitlist";
import moment from "moment-timezone";
import AuthForm from "../auth/AuthForm";
import SingleClass from "./SingleClass";
import CancelClass from "./CancelClass";
import useBranch from "../../hooks/useBranch";
import { Link, navigate } from "@reach/router";
import { AuthContext } from "../../context/AuthContext";
import { ModalContext } from "../../context/ModalContext";
import { canCancel, getValue, hideModal } from "../../utils";
import { ReservationsContext } from "../../context/ReservationsContext";
import { AvailableClassesContext } from "../../context/AvailableClassesContext";


const ScheduleClass = ({ singleClass, isHome }) => {
  const [needsLogin, setNeedsLogin] = useState(false);
  const { user, getUsuario } = useContext(AuthContext);
  const { available_classes } = useContext(AvailableClassesContext);
  const { clearModal, modalComponent } = useContext(ModalContext);
  const { reservations, postReservacion, cancelReservacion } =
    useContext(ReservationsContext);

  const { branch } = useBranch();

  const activeReservations = useMemo(
    () =>
      singleClass?.class_reservations?.filter(
        ({ deletedAt }) => deletedAt === null
      ) || 0,
    [singleClass]
  );
  const spots = useMemo(
    () => singleClass?.capacity - activeReservations,
    [singleClass, activeReservations]
  );

  useEffect(() => {
    if (user !== null && needsLogin) {
      clearModal();
      setTimeout(() => {
        setNeedsLogin(false);
        reservarClase();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const showClassModal = () => {
    if (isHome) {
      return navigate("/entrar");
    }
    if (isReserved()) {
      modalComponent(
        "Cancelar clase",
        <CancelClass
          singleClass={singleClass}
          reservations={reservations}
          cancelReservacion={(class_reservation_id) =>
            cancelReservacion(class_reservation_id, getUsuario)
          }
        />
      );
    }
  };

  const isFull = () => {
    if (singleClass !== null) {
      if (singleClass.capacity !== null && singleClass.class_reservations) {
        return spots <= 0;
      }
    }
  };

  const isReserved = () => {
    if (reservations && reservations !== null) {
      let class_ids = reservations
        .filter((reservation) => reservation.deletedAt === null)
        .map(({ single_class_id }) => single_class_id);
      return class_ids.includes(singleClass.single_class_id);
    }
    return false;
  };

  const reservarClase = () => {
    if (isHome) {
      return navigate(`/checkout/152`);
    }
    if (singleClass.class_package_id !== null) {
      return navigate(`/checkout/${singleClass.class_package_id}`);
    }
    if (isFull()) {
      return modalComponent(
        "Lista de Espera",
        <Waitlist
          handleCancel={clearModal}
          single_class_id={singleClass.single_class_id}
        />
      );
    }
    if (user === null) {
      setNeedsLogin(true);
      return modalComponent("Debes Acceder para Reservar", <AuthForm />, {
        no_padding: true,
      });
    }
    if (available_classes?.length > 0) {
      modalComponent(
        "Reservar Clase",
        <SingleClass
          clase={singleClass}
          hideModal={hideModal}
          postReservacion={(clase) => postReservacion(clase, getUsuario)}
        />,
        () => {},
        () => postReservacion(singleClass)
      );
    } else {
      modalComponent(
        "Créditos Agotados",
        <div className="container-fluid px-0">
          <p>
            Lo sentimos. Has agotado todos tus créditos para reservar Clases
            Presenciales. Puedes comprar más en Paquetes.
          </p>
          <p className="bold">
            Después de comprar, debes regresar a reservar tu clase.
          </p>
          <Link to="/mytbm/paquetes" className="btn w-100 btn-primary">
            Ir a Paquetes
          </Link>
        </div>
      );
    }
  };

  const renderBadges = () => {
    if (isFull()) {
      return <span className="badge badge-pill bg-dark">Sold Out</span>;
    }
    if (spots <= 3) {
      return (
        <span className="badge badge-pill bg-warning text-dark">
          {spots} spot{spots === 1 ? "" : "s"} left
        </span>
      );
    }
  };

  const renderButtons = () => {
    if (isReserved()) {
      return (
        <div className="row mt-2">
          <div className="container-fluid text-center">
            {canCancel(singleClass, branch) && (
              <button
                className="btn bg-dark-gray text-white btn-sm"
                onClick={showClassModal}
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      );
    }
    if (singleClass.class_package_id !== null) {
      return (
        <button
          className="btn btn-dark btn-sm mt-2"
          onClick={() => navigate(`/checkout/${singleClass.class_package_id}`)}
        >
          Comprar
        </button>
      );
    }
    const classTime = moment(singleClass.class_date).tz(branch.timezone, true);
    const diff = classTime.diff(moment().tz(branch.timezone), "hours");
    if (diff > 0) {
      return (
        <button
          className={`btn btn-sm mt-2 d-block m-auto ${
            isFull() ? "btn-outline-light" : "bg-dark-gray text-white"
          }`}
          onClick={reservarClase}
        >
          {isFull() ? "Lista de Espera" : "Reservar"}
        </button>
      );
    }
  };

  const renderInstructors = () => {
    return singleClass.class_instructors
      .filter(({ instructor }) => instructor !== null)
      .map((class_instructor) => (
        <span
          className={`badge badge-pill me-1 bg-${
            isFull() ? "primary-light" : "primary"
          }`}
          key={class_instructor.instructor_id}
        >
          {class_instructor.instructor.name}
        </span>
      ));
  };

  const renderClassType = () => {
    if (singleClass.class_type !== null) {
      return singleClass.class_type.name;
    }
  };

  const renderDescription = () => {
    if (
      singleClass.description &&
      singleClass.description !== "" &&
      singleClass.description !== null
    ) {
      return <p className="mb-1 small">{singleClass.description}</p>;
    }
  };

  return (
    <div
      className={`schedule-class ${
        singleClass.class_package_id !== null ? "bg-special" : ""
      } my-2 p-3 mx-2 ${
        isReserved()
          ? "bg-primary-light text-dark"
          : isFull()
          ? "bg-primary text-white"
          : "bg-light text-black"
      }`}
    >
      <p className="mb-1 bold">
        <i className={singleClass.icon} /> {renderClassType()}
      </p>
      {renderDescription()}
      <p className="font-weight-bold mb-1">
        <i className="far fa-clock"></i>{" "}
        {getValue(singleClass, "class_date", "time")}
      </p>
      <div className="mb-2">{renderInstructors()}</div>
      {renderBadges()}
      {renderButtons()}
    </div>
  );
};

export default ScheduleClass;
